import type { Direction, Position } from '@/features/letterParty/types';

const POSITION_EPSILON = 0.001;

function getDistance(from: Position, to: Position): number {
	return Math.hypot(to.x - from.x, to.y - from.y);
}

export function getMovedPosition(position: Position, direction: Direction, distance: number): Position {
	switch (direction) {
		case 'UP':
			return { x: position.x, y: position.y - distance };
		case 'DOWN':
			return { x: position.x, y: position.y + distance };
		case 'LEFT':
			return { x: position.x - distance, y: position.y };
		case 'RIGHT':
			return { x: position.x + distance, y: position.y };
	}
}

function getPathPositionAtDistance(path: Position[], distance: number): Position {
	if (path.length === 0) {
		return { x: 0, y: 0 };
	}

	let remainingDistance = distance;

	for (let index = 0; index < path.length - 1; index++) {
		const from = path[index];
		const to = path[index + 1];
		const segmentDistance = getDistance(from, to);

		if (segmentDistance <= POSITION_EPSILON) {
			continue;
		}

		if (remainingDistance <= segmentDistance) {
			return calculatePathStep(remainingDistance, segmentDistance, from, to);
		}

		remainingDistance -= segmentDistance;
	}

	return path[path.length - 1];
}

function calculatePathStep(remainingDistance: number, segmentDistance: number, from: Position, to: Position) {
	const progress = remainingDistance / segmentDistance;
	return {
		x: from.x + (to.x - from.x) * progress,
		y: from.y + (to.y - from.y) * progress,
	};
}

function getDirectionToward(from: Position, to: Position): Direction | null {
	const deltaX = to.x - from.x;
	const deltaY = to.y - from.y;

	if (Math.abs(deltaX) <= POSITION_EPSILON && Math.abs(deltaY) <= POSITION_EPSILON) {
		return null;
	}

	if (Math.abs(deltaX) > Math.abs(deltaY)) {
		return deltaX > 0 ? 'RIGHT' : 'LEFT';
	}

	return deltaY > 0 ? 'DOWN' : 'UP';
}

export function getPathDirectionAtDistance(path: Position[], distance: number): Direction | null {
	let remainingDistance = distance;

	for (let index = 0; index < path.length - 1; index++) {
		const from = path[index];
		const to = path[index + 1];
		const segmentDistance = getDistance(from, to);

		if (segmentDistance <= POSITION_EPSILON) {
			continue;
		}

		if (remainingDistance <= segmentDistance) {
			return getDirectionToward(to, from);
		}

		remainingDistance -= segmentDistance;
	}

	return null;
}

export function getSnakePositionsFromPath(path: Position[], length: number): Position[] {
	return Array.from({ length }, (_, index) => getPathPositionAtDistance(path, index));
}

export function trimSnakePath(path: Position[], snakeLength: number): Position[] {
	if (path.length <= 1) {
		return path;
	}

	const tailDistance = Math.max(0, snakeLength - 1);
	const trimmedPath = [path[0]];
	let coveredDistance = 0;

	for (let index = 0; index < path.length - 1; index++) {
		const from = path[index];
		const to = path[index + 1];
		const segmentDistance = getDistance(from, to);

		if (segmentDistance <= POSITION_EPSILON) {
			continue;
		}

		if (coveredDistance + segmentDistance >= tailDistance) {
			const remaining = tailDistance - coveredDistance;
			trimmedPath.push(calculatePathStep(remaining, segmentDistance, from, to));
			return trimmedPath;
		}

		coveredDistance += segmentDistance;
		trimmedPath.push(to);
	}

	return trimmedPath;
}

export function getTurnedPath(path: Position[]): Position[] {
	if (path.length === 0) {
		return path;
	}

	const head = path[0];
	const nextPoint = path[1];

	if (nextPoint && getDistance(head, nextPoint) <= POSITION_EPSILON) {
		return path;
	}

	return [head, ...path];
}
